import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmt, fmtCompact } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/Primitives";
import { BRANDING } from "@/config/branding";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, Wallet, PiggyBank, Inbox, Compass, ArrowRight, ArrowLeftRight,
} from "lucide-react";

const GOLD = "#C6A45C";
const DONUT = BRANDING.chartPalette;

function ChartTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-[#EAE0D2] rounded-xl p-3 shadow-lg text-xs">
      {label && <div className="text-[#8A7F70] mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-data">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-[#6b6256] capitalize">{p.name}:</span>
          <span className="text-[#2C2A28]">{fmt(p.value, currency)}</span>
        </div>
      ))}
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent, delay }) {
  return (
    <Card className="fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#8A7F70]">{label}</div>
        <Icon size={18} strokeWidth={1.5} className="text-[#C6A45C]" />
      </div>
      <div className="font-display text-3xl mt-4 font-light tabular-nums text-[#2C2A28]">{value}</div>
      {sub && <div className={`text-xs mt-2 ${accent || "text-[#8A7F70]"}`}>{sub}</div>}
    </Card>
  );
}

function HomeEmpty({ user, navigate }) {
  const tiles = [
    { icon: Inbox, title: "Add your first document", desc: "Receipts, bills, statements — filed for you.", to: "/inbox", cta: "Add to your home" },
    { icon: ArrowLeftRight, title: "Record income or an expense", desc: "Start seeing your cash flow take shape.", to: "/cash-flow", cta: "Add a transaction" },
    { icon: PiggyBank, title: "Create your first savings goal", desc: "Set a target and watch it grow.", to: "/savings", cta: "Create a goal" },
  ];
  return (
    <div>
      <div className="mb-8 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A99F90] mb-1">Welcome home</div>
        <h1 className="font-display text-3xl sm:text-4xl font-light text-[#2C2A28]">Good day, {user?.name?.split(" ")[0] || "there"}</h1>
        <p className="text-[#8A7F70] text-sm mt-2">Let's set up your Financial Home — start with any one of these.</p>
      </div>

      <Card className="overflow-hidden mb-6 fade-up" data-testid="home-empty">
        <div className="grid md:grid-cols-2 items-center gap-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-light text-[#2C2A28] leading-snug mb-3">
              Your home is ready. Let's bring it to life.
            </h2>
            <p className="text-[#8A7F70] text-sm leading-relaxed">
              Everything you add stays organised in one calm, private place. There's nothing to set up —
              simply add your first piece of information and your dashboard fills in beautifully.
            </p>
          </div>
          <img src="https://static.prod-images.emergentagent.com/jobs/f79b4fed-1fc2-47a6-90b0-9b9170b69083/images/17b1cf7aa9f4e6a2249e57d4faf69546e20c457449b51f479388f10106fd5854.png"
            alt="" className="w-full max-w-sm mx-auto rounded-2xl" />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {tiles.map((t, i) => (
          <div key={t.to} data-testid={`empty-cta-${i}`} onClick={() => navigate(t.to)}
            className="rounded-2xl p-7 cursor-pointer border border-[#EAE0D2] bg-white/60 hover:bg-white hover:-translate-y-0.5 transition-all fade-up group"
            style={{ animationDelay: `${i * 80}ms` }}>
            <div className="w-11 h-11 rounded-full bg-[#F0E7D8] flex items-center justify-center mb-4">
              <t.icon size={20} className="text-[#C6A45C]" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-lg text-[#2C2A28] mb-1">{t.title}</h3>
            <p className="text-sm text-[#8A7F70] leading-relaxed">{t.desc}</p>
            <div className="mt-4 inline-flex items-center gap-2 gold-text text-sm group-hover:gap-3 transition-all">{t.cta} <ArrowRight size={15} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const { user, hasModule } = useAuth();
  const navigate = useNavigate();
  const cur = user?.currency || "AUD";
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-[#8A7F70] animate-pulse">Opening your Financial Home…</div>;
  const k = d.kpis;
  const hasData = !!(k.net_worth || k.income || k.expense || k.savings || k.portfolio_value ||
    k.documents || d.monthly.length || d.by_category.length);

  if (!hasData) return <HomeEmpty user={user} navigate={navigate} />;

  return (
    <div>
      <div className="mb-8 fade-up">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A99F90] mb-1">Welcome home</div>
        <h1 className="font-display text-3xl sm:text-4xl font-light text-[#2C2A28]">
          Good day, {user?.name?.split(" ")[0] || "there"}
        </h1>
        <p className="text-[#8A7F70] text-sm mt-2">Your Financial Home is organised and up to date.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <KPI label="Net Worth" value={fmt(k.net_worth, cur)} icon={Wallet} delay={0} sub="Across everything you own" />
        <KPI label="Cash Flow" value={fmt(k.cash_flow, cur)} icon={k.cash_flow >= 0 ? TrendingUp : TrendingDown}
          accent={k.cash_flow >= 0 ? "text-[#5a9a6a]" : "text-[#b06b6b]"} delay={60} sub={`Savings rate ${k.savings_rate}%`} />
        <KPI label="Savings" value={fmt(k.savings, cur)} icon={PiggyBank} delay={120} sub="Toward your goals" />
        <KPI label="Inbox" value={`${k.documents}`} icon={Inbox} delay={180}
          accent={k.documents_pending ? "text-[#b8924a]" : "text-[#8A7F70]"}
          sub={k.documents_pending ? `${k.documents_pending} awaiting review` : "All documents filed"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <Card className="lg:col-span-2 fade-up" style={{ animationDelay: "220ms" }}>
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl font-normal text-[#2C2A28]">Cash Flow</h3>
            <span className="text-xs text-[#A99F90] uppercase tracking-wider">Last 6 months</span>
          </div>
          {d.monthly.length === 0 ? (
            <div className="text-[#A99F90] text-sm py-16 text-center">Add income and expenses to see your flow.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.monthly} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,160,120,0.15)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#A99F90", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#A99F90", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                <Tooltip cursor={{ fill: "rgba(198,164,92,0.06)" }} content={<ChartTooltip currency={cur} />} />
                <Bar dataKey="income" fill="#8FB996" radius={[6, 6, 0, 0]} maxBarSize={26} />
                <Bar dataKey="expense" fill="#C99FA0" radius={[6, 6, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="fade-up" style={{ animationDelay: "280ms" }}>
          <h3 className="font-display text-xl font-normal mb-2 text-[#2C2A28]">Spend by Category</h3>
          {d.by_category.length === 0 ? (
            <div className="text-[#A99F90] text-sm py-20 text-center">No expenses yet.</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={d.by_category} dataKey="value" nameKey="name" innerRadius={52} outerRadius={86} paddingAngle={2} stroke="none">
                    {d.by_category.map((_, i) => <Cell key={i} fill={DONUT[i % DONUT.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip currency={cur} />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {d.by_category.slice(0, 5).map((c, i) => (
                  <div key={c.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[#6b6256]">
                      <span className="w-2 h-2 rounded-full" style={{ background: DONUT[i % DONUT.length] }} />{c.name}
                    </span>
                    <span className="font-mono-data text-[#8A7F70]">{fmt(c.value, cur)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 fade-up" style={{ animationDelay: "320ms" }}>
          <h3 className="font-display text-xl font-normal mb-6 text-[#2C2A28]">Net Trend</h3>
          {d.monthly.length === 0 ? (
            <div className="text-[#A99F90] text-sm py-16 text-center">Your trend appears as you add activity.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={d.monthly}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(180,160,120,0.15)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#A99F90", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#A99F90", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtCompact(v, cur)} />
                <Tooltip content={<ChartTooltip currency={cur} />} />
                <Area type="monotone" dataKey="net" stroke={GOLD} strokeWidth={2.5} fill="url(#goldFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <div className="space-y-5">
          {hasModule("ai_coach") && (
            <div onClick={() => navigate("/insights")} data-testid="home-insights-cta"
              className="relative overflow-hidden rounded-2xl p-7 cursor-pointer border border-[#C6A45C]/30 fade-up group"
              style={{ background: "linear-gradient(135deg, #FBF4E6, #FBF7F1)", animationDelay: "360ms" }}>
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#C6A45C]/15 blur-3xl" />
              <Compass className="text-[#C6A45C] mb-3" size={24} strokeWidth={1.5} />
              <h3 className="font-display text-xl font-light text-[#2C2A28] mb-2">Your advisory team</h3>
              <p className="text-sm text-[#8A7F70] leading-relaxed">A calm review from your planner, accountant and bank manager.</p>
              <div className="mt-4 inline-flex items-center gap-2 gold-text text-sm group-hover:gap-3 transition-all">Open Insights <ArrowRight size={16} /></div>
            </div>
          )}
          <div onClick={() => navigate("/inbox")} data-testid="home-inbox-cta"
            className="rounded-2xl p-7 cursor-pointer border border-[#EAE0D2] bg-white/60 hover:bg-white transition-all fade-up group" style={{ animationDelay: "400ms" }}>
            <Inbox className="text-[#C6A45C] mb-3" size={24} strokeWidth={1.5} />
            <h3 className="font-display text-xl font-light text-[#2C2A28] mb-2">Financial Inbox</h3>
            <p className="text-sm text-[#8A7F70] leading-relaxed">Add receipts & documents — we'll file them for you.</p>
            <div className="mt-4 inline-flex items-center gap-2 gold-text text-sm group-hover:gap-3 transition-all">Add to your home <ArrowRight size={16} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
